import { useAuth0 } from "@auth0/auth0-react"
import { useNavigate } from "react-router-dom"
import {
	useRef,
} from "react"
import {IconButton, Tooltip} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import {
	Menu,
	Item,
	Separator,
	Submenu, useContextMenu
} from "react-contexify"
import "react-contexify/dist/ReactContexify.css"
import {
	Close,
	Launch,
	LiveHelp,
	Logout,
	ManageAccounts, OndemandVideo,
	SettingsApplicationsOutlined, Splitscreen,
} from "@mui/icons-material"
import {useDispatch, useSelector} from "react-redux"
import {
	openContactModal,
	openHelpModal,
	openWorkspaceManager,
	openWorkspacePermissions
} from "../reducers/dashboardModalControlSlice"
import {fetchUserWorkspaces, selectIsFetchingUserWorkspaces, selectUserWorkspaces} from "../reducers/userSlice"
import Loading from "./Loading"
import {
	leaveSecondaryWorkspace,
	openSecondaryWorkspace,
	selectShowSecondaryWorkspace
} from "../reducers/workspaceSlice"
import {createPortal} from "react-dom"
import {disconnectAllSocketServices} from "../context/socket.context"

export function DashboardMenu({ onDashboard, }) {
	const dispatch = useDispatch()
	const dashboardMenuRef = useRef(null)
	const {
		show,
		hideAll
	} = useContextMenu({
		id: "dashboard-menu"
	})

	const handleClick = (event) => {
		const dashMenuOpen = document.querySelector(".dashboard-menu-open")
		if(dashMenuOpen) {
			hideAll()
		} else {
			dispatch(fetchUserWorkspaces())
			const { right, bottom } = dashboardMenuRef.current.getBoundingClientRect()
			show({
				event,
				position: {
					x: right,
					y: onDashboard ? bottom + 40 : bottom
				},
			})
		}
	}

	return (
		<>
			<Tooltip title="Dashboard Menu">
				<IconButton
					sx={{ p: "10px", flexGrow: 0 }}
					color="default"
					aria-controls={"dashboard-positioned-menu"}
					aria-haspopup="true"
					aria-expanded={"true"}
					onClick={handleClick}
					ref={dashboardMenuRef}
				>
					<MenuIcon />
				</IconButton>
			</Tooltip>
		</>
	)
}

export function DashboardMenuWindow({onDashboard}) {
	const dispatch = useDispatch()
	const { logout } = useAuth0()
	const navigate = useNavigate()
	const showingSecondaryWorkspace = useSelector(selectShowSecondaryWorkspace)
	const isLoadingUserWorkspaces = useSelector(selectIsFetchingUserWorkspaces)
	const userWorkspaces = useSelector(selectUserWorkspaces)
	const {hideAll} = useContextMenu({
		id: "dashboard-menu"
	})
	const handleClose = () => {hideAll()}

	const switchWorkspace = () => {
		dispatch(openWorkspaceManager())
		handleClose()
	}

	const contactSupport = () => {
		dispatch(openContactModal())
		handleClose()
	}

	const handleWorkspacePermissions = () => {
		dispatch(openWorkspacePermissions())
		handleClose()
	}

	const handleInstructions = () => {
		dispatch(openHelpModal())
		handleClose()
	}

	const handleOpenSecondaryWorkspace = (userWorkspace) => {
		const {workspace, read, write, admin, ownerIsFreeTier, ownerIsSuperAdmin} = userWorkspace
		dispatch(openSecondaryWorkspace({
			...workspace,
			permissions: { read, write, admin },
			ownerIsFreeTier,
			ownerIsSuperAdmin
		}))
		handleClose()
	}

	const handleCloseSecondaryWorkspace = () => {
		dispatch(leaveSecondaryWorkspace())
		handleClose()
	}

	const handleLogout = () => {
		disconnectAllSocketServices()
		logout({ returnTo: window.location.origin })
	}

	return (
		<Menu id={"dashboard-menu"}
			style={
				{
					zIndex: 503, // one higher than the gutters in Dashboard
				}
			}
			className={"dashboard-menu-open"}
		>
			{ onDashboard && (
				<>
					<Item
						onClick={switchWorkspace}
					>
						<Launch fontSize={"small"} sx={{ marginRight: "5px" }} /> Switch
						workspace
					</Item>
					<Item onClick={handleWorkspacePermissions}>
						<SettingsApplicationsOutlined fontSize={"small"} sx={{ marginRight: "5px" }}/>
						Manage this workspace
					</Item>
					<Separator />
					<Submenu
						label={
							<>
								<Splitscreen fontSize={"small"} sx={{ marginRight: "5px" }}/>
								Secondary Workspace
							</>
						}
						style={{
							maxHeight: "300px",
							overflowY: "auto",
							overflowX: "hidden"
						}}
					>
						{showingSecondaryWorkspace && (
							<>
								<Item onClick={handleCloseSecondaryWorkspace}>
									<Close fontSize={"small"} sx={{ marginRight: "5px" }}/>
									Close Secondary Workspace
								</Item>
								<Separator />
							</>
						)}
						{ isLoadingUserWorkspaces && (
							<Item>
								<Loading />
							</Item>
						)}
						{ !isLoadingUserWorkspaces && userWorkspaces && userWorkspaces.map(userWorkspace => (
							<Item key={userWorkspace.workspaceId} onClick={() => handleOpenSecondaryWorkspace(userWorkspace)}>
								{userWorkspace.workspace.title}
							</Item>
						))}
					</Submenu>
				</>
			)}
			<Item onClick={() => navigate("/account-settings")}>
				<ManageAccounts fontSize={"small"} sx={{ marginRight: "5px" }} />{" "}
				Account settings
			</Item>
			<Separator/>
			<Item onClick={handleInstructions}>
				<OndemandVideo fontSize={"small"} sx={{ marginRight: "5px" }} />
				Training Videos
			</Item>
			<Item onClick={contactSupport}>
				<LiveHelp fontSize={"small"} sx={{ marginRight: "5px" }} />
				Contact Support
			</Item>
			<Separator />
			<Item onClick={handleLogout}>
				<Logout fontSize={"small"} sx={{ marginRight: "5px" }} />
				Logout
			</Item>
		</Menu>
	)
}

/**
 * Wrapper for a createPortal call. This prevents
 * z-index errors with ReactSplit library.
 *
 * Portal rendered inside <Dashboard />
 */
export function DashboardMenuPortal(props) {
	return createPortal(<DashboardMenuWindow {...props}/>, document.body)
}